import { ProductAttributeInterface } from '../../Interface/ProductAttribute';
import { ProductSizeInterface } from '../../Interface/ProductSize';

/**
 * Einheitliche Formatierung eines Rabatt-Prozentwerts: ganzzahlig ohne
 * Nachkommastelle, sonst mit einer Nachkommastelle (und ohne ".0").
 */
export function formatDiscountPercent(percent: number): string {
  return Number.isInteger(percent)
    ? percent.toString()
    : percent.toFixed(1).replace(/\.0$/, '');
}

const PERCENT_ONLY_REASON = /^\d+(\.\d+)?\s*%\s*off$/i;

/**
 * Liefert den anzuzeigenden Clearance-Grund oder undefined, wenn kein echter
 * Grund vorliegt bzw. der Backend-Text nur den Prozentwert wiederholt ("X% Off").
 */
export function cleanClearanceReason(raw?: string | null): string | undefined {
  const trimmed = raw?.trim();
  return trimmed && !PERCENT_ONLY_REASON.test(trimmed) ? trimmed : undefined;
}

export interface FlavourDiscount {
  /** true, sobald der Flavour eine Clearance-Variante ist. */
  isClearance: boolean;
  /**
   * Effektiver, kombinierter Rabatt dieses Flavours (Clearance + ggf. reguläre
   * Promo). Für ausgeloggte Nutzer enthält dies nur den Clearance-Anteil, da
   * reguläre Promos serverseitig nicht an Gäste ausgeliefert werden.
   */
  percent: number;
  percentLabel: string;
  /**
   * Clearance-Grund (z.B. "Short Expiry"). Undefined, wenn kein eigener Grund
   * vorliegt bzw. der Backend-Text nur den Prozentwert wiederholt ("X% Off").
   */
  reason?: string;
  /** Text für den Störer/Badge, z.B. "27%". */
  badgeLabel: string;
  /** Tooltip-Text, kombiniert Grund und Prozentwert. */
  tooltip: string;
  /**
   * Kombinierter (reduzierter) Preis. Undefined für Gäste bzw. wenn kein
   * personalisierter Preis vorliegt.
   */
  combinedPrice?: number;
}

/**
 * Zentrale Ableitung der Clearance-/Rabatt-Darstellung eines einzelnen Flavours
 * (FID). Einzige Quelle der Wahrheit für Badge, Grund und Tooltip in Liste,
 * Tabelle und Info-Popup (P-4877). Ersetzt die zuvor dreifach duplizierte Logik
 * in AttributePrice, AttributeBrandName und ProductQuickViewModal.
 */
export function getFlavourDiscount(
  attribute: Pick<
    ProductAttributeInterface,
    | 'clearance_discount_percent'
    | 'combined_discount_percent'
    | 'combined_description_1'
    | 'combined_price'
  >
): FlavourDiscount {
  const isClearance = (attribute.clearance_discount_percent ?? 0) > 0;
  const percent =
    attribute.combined_discount_percent ??
    attribute.clearance_discount_percent ??
    0;
  const percentLabel = formatDiscountPercent(percent);

  // Wenn der Backend-Grund nur den Prozentwert wiederholt ("27% Off"), taucht er
  // ohnehin schon im Badge auf – dann keinen redundanten Grund unter dem Flavour.
  const reason = cleanClearanceReason(attribute.combined_description_1);

  const badgeLabel = `${percentLabel}%`;
  const tooltip = reason
    ? `${reason} / ${percentLabel}% Off`
    : `${percentLabel}% Off`;

  return {
    isClearance,
    percent,
    percentLabel,
    reason,
    badgeLabel,
    tooltip,
    combinedPrice: attribute.combined_price,
  };
}

export interface ClearancePercentAggregate {
  /** true, wenn die PID mindestens eine Clearance-Variante hat. */
  hasClearance: boolean;
  /** true, wenn ALLE Flavours reduziert sind (kein Flavour mit 0 % Rabatt). */
  allReduced: boolean;
  /** Höchster effektiver Rabatt über alle Flavours. */
  maxPercent: number;
  /** true, wenn sich die (positiven) effektiven Rabatte zwischen Flavours unterscheiden. */
  hasVaried: boolean;
  /**
   * Störer-Text für die Produktebene: "Up to X% Off" bei Varianz, sonst "X% Off".
   * Nur gesetzt, wenn ALLE Flavours reduziert sind – ist nur ein Teil in Clearance,
   * bleibt die Produktebene neutral (null).
   */
  label: string | null;
}

/**
 * PID-Aggregat des Clearance-/Kombi-Rabatts über alle Flavours (P-4877, Punkt 2).
 * Grundlage für den PID-Störer ("Up to X%") und für die Entscheidung, ob pro
 * Flavour der einzelne Prozentwert gezeigt wird (nur bei unterschiedlichen
 * Rabatten). Greift nur, wenn die PID überhaupt eine Clearance-Variante hat –
 * reine Promo-Produkte bleiben unberührt.
 */
export function getClearancePercentAggregate(
  attributes: Array<
    Pick<
      ProductAttributeInterface,
      'clearance_discount_percent' | 'combined_discount_percent'
    >
  >
): ClearancePercentAggregate {
  const hasClearance = attributes.some(
    (a) => (a.clearance_discount_percent ?? 0) > 0
  );
  if (!hasClearance) {
    return {
      hasClearance: false,
      allReduced: false,
      maxPercent: 0,
      hasVaried: false,
      label: null,
    };
  }

  const effectivePercents = attributes.map(
    (a) => a.combined_discount_percent ?? a.clearance_discount_percent ?? 0
  );
  const positives = effectivePercents.filter((p) => p > 0);
  if (positives.length === 0) {
    return {
      hasClearance: true,
      allReduced: false,
      maxPercent: 0,
      hasVaried: false,
      label: null,
    };
  }

  const maxPercent = Math.max(...positives);
  const allReduced = positives.length === effectivePercents.length;
  const hasVaried = new Set(positives).size > 1;

  // Produktebenen-Störer nur, wenn ALLE Flavours reduziert sind. Ist nur ein Teil
  // in Clearance, bleibt die Produktebene neutral – der Rabatt steht ausschließlich
  // pro Flavour (P-4877, Sales-Nachtrag 24.08.2026).
  const label = allReduced
    ? `${hasVaried ? 'Up to ' : ''}${formatDiscountPercent(maxPercent)}% Off`
    : null;

  return { hasClearance: true, allReduced, maxPercent, hasVaried, label };
}

/**
 * Welche Art von Rabatt-Kommunikation ein Produkt (PID) auf Produktebene bekommt.
 * - 'promotion': Die PID hat eine sichtbare Promotion (Discount- ODER Plus-Typ).
 * - 'clearance': Keine Promotion, aber mindestens eine Clearance-Variante.
 * - 'none': Weder Promotion noch Clearance.
 */
export type ProductSizeDiscountKind = 'promotion' | 'clearance' | 'none';

export interface ProductSizeDiscountDisplay {
  kind: ProductSizeDiscountKind;
  /** Promo-Text für den PID-Störer (nur bei kind === 'promotion'). */
  promoText: string | null;
  /** Clearance-Aggregat-Label für den PID-Störer, z.B. "Up to 50% Off" (nur bei kind === 'clearance'). */
  clearanceLabel: string | null;
  /**
   * true → PID-Preis als niedrigsten Clearance-Preis (mit "From" bei Preis-
   * varianz); false → normaler Produktpreis (auch bei Promotion).
   */
  useClearancePrice: boolean;
  /**
   * true → pro Flavour den (kombinierten) Rabatt inkl. Preis anzeigen. Bei
   * einheitlichem Clearance-Rabatt ohne Promotion steht der Prozentwert nur im
   * PID-Störer, daher false; bei Promotion oder unterschiedlichen Rabatten true.
   */
  showFlavourDiscount: boolean;
}

/**
 * Zentrale Entscheidung, wie ein Produkt (PID) Rabatte kommuniziert – einzige
 * Quelle der Wahrheit für PID-Störer (ProductNameRow), PID-Preis (ProductSizePrice)
 * und die Pro-Flavour-Anzeige (ProductAttribute). Damit laufen diese drei Stellen
 * nicht mehr auseinander (P-4877).
 *
 * "Hat Promotion" stützt sich bewusst auf promo_percent / promo_free_product_count:
 * Das Backend (ProductSizeNormalizer) setzt diese Felder ausschließlich im
 * Promotion-Zweig (Discount-Typ: promo_percent > 0, Plus-Typ:
 * promo_free_product_count > 0). Bei Clearance-Produkten und für Gäste bleiben sie
 * ungesetzt – daher sind sie ein verlässlicher Indikator und unabhängig von
 * promo_plus_condition_count (das auch bei Discount-Promos ≥ 1 ist).
 *
 * Regeln (siehe P-4877, inkl. Sales-Nachtrag 24.08.2026):
 * 1. Promotion vorhanden  → Störer zeigt die Promotion, Preis normal,
 *    Clearance-Info nur pro Flavour (kombinierter Rabatt + Preis).
 * 2. keine Promotion, ALLE Flavours reduziert:
 *    2a. gleiche Rabatte  → Störer "X% Off", Preis, pro Flavour nur der Grund.
 *    2b. unterschiedliche → Störer "Up to X% Off" + "From"-Preis, pro Flavour
 *        Rabatt + Preis.
 * 3. keine Promotion, nur ein Teil der Flavours in Clearance → KEINE Kommunikation
 *    auf Produktebene (kein Störer, kein Streich-/reduzierter Preis, nur der
 *    reguläre Preis); der Rabatt erscheint ausschließlich pro Flavour.
 */
export function getProductSizeDiscountDisplay(
  product_size: Pick<
    ProductSizeInterface,
    | 'promo_text'
    | 'promo_percent'
    | 'promo_free_product_count'
    | 'product_attributes'
  >
): ProductSizeDiscountDisplay {
  const hasDiscountPromotion = (product_size.promo_percent ?? 0) > 0;
  const hasPlusPromotion = (product_size.promo_free_product_count ?? 0) > 0;
  const hasPromotion = hasDiscountPromotion || hasPlusPromotion;

  if (hasPromotion) {
    return {
      kind: 'promotion',
      promoText: product_size.promo_text ?? null,
      clearanceLabel: null,
      useClearancePrice: false,
      showFlavourDiscount: true,
    };
  }

  const clearance = getClearancePercentAggregate(
    product_size.product_attributes ?? []
  );
  if (clearance.hasClearance && clearance.maxPercent > 0) {
    // Nur ein Teil der Flavours in Clearance → Produktebene bleibt neutral, der
    // Rabatt steht ausschließlich pro Flavour (P-4877, Nachtrag 24.08.2026).
    if (!clearance.allReduced) {
      return {
        kind: 'clearance',
        promoText: null,
        clearanceLabel: null,
        useClearancePrice: false,
        showFlavourDiscount: true,
      };
    }
    return {
      kind: 'clearance',
      promoText: null,
      clearanceLabel: clearance.label,
      useClearancePrice: true,
      showFlavourDiscount: clearance.hasVaried,
    };
  }

  return {
    kind: 'none',
    promoText: null,
    clearanceLabel: null,
    useClearancePrice: false,
    showFlavourDiscount: false,
  };
}
